import React, { FC, useEffect, useRef, useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useRouter } from 'next/router';
import { useToast } from '@common/toast';
import Icon from '@common/assets';
import BackgroundArea from '@components/profile/profileOverlay/overlays/common/backgroundArea';
import SkillTab from '@components/profile/profileOverlay/overlays/skill/SkillTab';
import LicensesForm from '@components/profile/profileOverlay/overlays/skill/skillWrite/licensesForm';
import ExaminationsForm from '@components/profile/profileOverlay/overlays/skill/skillWrite/examinationsForm';
import SkillKeywordForm from '@components/profile/profileOverlay/overlays/skill/skillWrite/skillKeywordForm';
import ExitModal from '@components/profile/profileOverlay/exitModal';
import { useUserInfo } from '@repository/auth/useUserInfo';
import useProfileSkill, { useSaveProfileSkill, viewKeys } from '@repository/profiles/useProfileSkill';
import ProfileSkillVO from '@domain/vo/profile/ProfileSkillVO';
import Colors from '@domain/constant/colors';
import { queryClient } from '@utils/queryClient';
import { KeyboardEventUtil } from '@utils/KeyboardEventUtil';

import classnames from 'classnames/bind';
import styles from './skill.module.scss';
const cx = classnames.bind(styles);

interface ISkillOverlay {
  visible: boolean;
  onClose: () => void;
}

const skillDefaultValues: RecursivePartial<ProfileSkillVO> = {
  licenses: [],
  examinations: [],
  skills: [],
};

export type SkillTabType = 'LICENSE' | 'EXAMINATION' | 'SKILL_KEYWORD';

const SkillOverlay: FC<ISkillOverlay> = ({ visible, onClose }) => {
  const Toast = useToast();
  const { query } = useRouter();
  const { data: userInfo } = useUserInfo();
  const { data: profileSkill } = useProfileSkill(!!userInfo?.id && visible);

  const { mutateAsync: saveProfileSkillMutateAsync } = useSaveProfileSkill();

  const methods = useForm<ProfileSkillVO>({
    defaultValues: skillDefaultValues as ProfileSkillVO,
  });
  const {
    reset,
    handleSubmit,
    formState: { isDirty },
  } = methods;

  const scrollRef = useRef<HTMLDivElement>(null);
  const [selectedTabType, setSelectedTabType] = useState<SkillTabType>('LICENSE');
  const isSelectedTab = (skillTabType: SkillTabType) => skillTabType === selectedTabType;
  const [exitModalVisible, setExitModalVisible] = useState(false);

  const onCloseOverlay = () => {
    if (isDirty) {
      setExitModalVisible(true);
      return;
    }

    onClose();
  };

  const onSubmit: SubmitHandler<ProfileSkillVO> = (data) => {
    saveProfileSkillMutateAsync(Object.assign(data, { sourcePage: query.source_page }))
      .then(async (result) => {
        if (result) {
          await queryClient.refetchQueries(viewKeys, { active: true, exact: true });
          onClose();
          Toast({ type: 'success', iconType: 'info', content: '성공적으로 저장되었습니다.' });
        } else {
          Toast({ type: 'error', iconType: 'info', content: '저장에 필요한 정보를 작성해주세요.' });
        }
      })
      .catch(() => {
        Toast({ type: 'error', iconType: 'info', content: '저장에 필요한 정보를 작성해주세요.' });
      });
  };

  useEffect(() => {
    if (profileSkill) {
      reset(profileSkill);
    }
  }, [profileSkill]);

  useEffect(() => {
    if (visible) {
      setSelectedTabType('LICENSE');
    }
  }, [visible]);

  return (
    <>
      <BackgroundArea visible={visible} onClickArea={onCloseOverlay} />
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} onKeyDown={KeyboardEventUtil.onCheckKeyDownPreventEnterExceptTextarea}>
          <div className={cx('overlayArea', { on: visible })}>
            <div className={cx('overlayInner')}>
              <div className={cx('overlayTitleArea')}>
                {/* 상단 탭 영역 */}
                <SkillTab selectedTabType={selectedTabType} setSelectedTabType={setSelectedTabType} />

                <strong className={cx('overlayTitle')}>보유하고 있는 지식 · 기술을 입력해주세요.</strong>
                <p className={cx('desc')}>
                  <Icon name="informationLight" width={20} height={20} fill={Colors.C_COOL_GRAY_70} />
                  기업에서 가장 중요하게 보는 항목 중 하나이므로 최대한 많이 기입해주세요.
                </p>
              </div>

              <div className={cx('scrollArea')}>
                <div ref={scrollRef} className={cx('scrollInner')}>
                  <div className={cx('scrollWrap')}>
                    {/* 자격증 */}
                    <LicensesForm visible={isSelectedTab('LICENSE')} />
                    {/* 공인 어학시험 */}
                    <ExaminationsForm visible={isSelectedTab('EXAMINATION')} />
                    {/* 기술 키워드 */}
                    <SkillKeywordForm scrollRef={scrollRef} visible={isSelectedTab('SKILL_KEYWORD')} />
                  </div>
                </div>
              </div>

              <div className={cx('btnArea')}>
                <a className={cx('btn', 'cancel')} role="button" onClick={onCloseOverlay}>
                  나가기
                </a>
                <button className={cx('btn', 'confirm')} type="submit">
                  저장
                </button>
              </div>
            </div>

            {/* 나가기 모달 영역 */}
            <ExitModal visible={exitModalVisible} onClose={() => setExitModalVisible(false)} onExit={onClose} />
          </div>
        </form>
      </FormProvider>
    </>
  );
};

export default SkillOverlay;
